import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { X } from 'lucide-react';
import { categoriasCostoFijoAPI, costosFijosAPI } from '../services/api';
import { selectStyles, EditableInput } from '../utils/adminShared';

const CostosFijosPanel = () => {
    const [loading, setLoading] = useState(true);
    const [categoriasCostoFijo, setCategoriasCostoFijo] = useState([]);
    const [costosFijos, setCostosFijos] = useState([]);
    const [nuevoCostoFijo, setNuevoCostoFijo] = useState({ nombre: '', costo: 0, categoria_id: '' });
    const [nuevaCategoriaCF, setNuevaCategoriaCF] = useState({ nombre: '' });
    const [showCategoriasCFModal, setShowCategoriasCFModal] = useState(false);

    useEffect(() => {
        cargarDatos();
    }, []);

    const cargarDatos = async () => {
        setLoading(true);
        try {
            const [categoriasCFRes, costosFijosRes] = await Promise.all([
                categoriasCostoFijoAPI.getAll(true),
                costosFijosAPI.getAll(true)
            ]);
            setCategoriasCostoFijo(categoriasCFRes.data || []);
            setCostosFijos(costosFijosRes.data || []);
        } catch (err) {
            console.error('Error al cargar datos:', err);
        } finally {
            setLoading(false);
        }
    };

    // === CATEGORÍAS DE COSTO FIJO ===
    const agregarCategoriaCF = async () => {
        if (!nuevaCategoriaCF.nombre.trim()) {
            toast.error('El nombre es obligatorio');
            return;
        }
        try {
            await categoriasCostoFijoAPI.create(nuevaCategoriaCF);
            setNuevaCategoriaCF({ nombre: '' });
            cargarDatos();
            toast.success('Categoría creada');
        } catch (err) {
            toast.error('Error: ' + err.message);
        }
    };

    const actualizarCategoriaCF = async (id, data) => {
        try {
            await categoriasCostoFijoAPI.update(id, data);
            setCategoriasCostoFijo(prev => prev.map(c => c.id === id ? { ...c, ...data } : c));
        } catch (err) {
            toast.error('Error: ' + err.message);
            cargarDatos();
        }
    };

    const eliminarCategoriaCF = (id, nombre) => {
        toast((t) => (
            <div className="toast-confirm">
                <p>¿Eliminar la categoría <strong>{nombre}</strong>?</p>
                <div className="toast-confirm-buttons">
                    <button className="toast-btn toast-btn-cancel" onClick={() => toast.dismiss(t.id)}>
                        Cancelar
                    </button>
                    <button
                        className="toast-btn toast-btn-danger"
                        onClick={async () => {
                            toast.dismiss(t.id);
                            try {
                                await categoriasCostoFijoAPI.delete(id);
                                toast.success('Categoría eliminada');
                                cargarDatos();
                            } catch (err) {
                                toast.error('Error: ' + err.message);
                            }
                        }}
                    >
                        Eliminar
                    </button>
                </div>
            </div>
        ), { duration: Infinity });
    };

    // === COSTOS FIJOS ===
    const agregarCostoFijo = async () => {
        if (!nuevoCostoFijo.nombre.trim()) {
            toast.error('El nombre es obligatorio');
            return;
        }
        if (!nuevoCostoFijo.categoria_id) {
            toast.error('La categoría es obligatoria');
            return;
        }
        try {
            await costosFijosAPI.create(nuevoCostoFijo);
            setNuevoCostoFijo({ nombre: '', costo: 0, categoria_id: '' });
            cargarDatos();
            toast.success('Costo fijo creado');
        } catch (err) {
            toast.error('Error: ' + err.message);
        }
    };

    const actualizarCostoFijo = async (id, data) => {
        try {
            await costosFijosAPI.update(id, data);
            setCostosFijos(prev => prev.map(cf => cf.id === id ? { ...cf, ...data } : cf));
        } catch (err) {
            toast.error('Error: ' + err.message);
            cargarDatos();
        }
    };

    const eliminarCostoFijo = (id, nombre) => {
        toast((t) => (
            <div className="toast-confirm">
                <p>¿Eliminar el costo fijo <strong>{nombre}</strong>?</p>
                <div className="toast-confirm-buttons">
                    <button className="toast-btn toast-btn-cancel" onClick={() => toast.dismiss(t.id)}>
                        Cancelar
                    </button>
                    <button
                        className="toast-btn toast-btn-danger"
                        onClick={async () => {
                            toast.dismiss(t.id);
                            try {
                                await costosFijosAPI.delete(id);
                                toast.success('Costo fijo eliminado');
                                cargarDatos();
                            } catch (err) {
                                toast.error('Error: ' + err.message);
                            }
                        }}
                    >
                        Eliminar
                    </button>
                </div>
            </div>
        ), { duration: Infinity });
    };

    if (loading) {
        return <div className="loading">Cargando costos fijos...</div>;
    }

    return (
    <>
        <div className="card">
            <div className="card-header">
                <h2>Costos Fijos</h2>
            </div>
            <div className="admin-content">
                <div className="admin-section-header">
                    <h3 className="h3">Agregar Costo Fijo</h3>
                    <button
                        className="btn ghost small"
                        type="button"
                        onClick={() => setShowCategoriasCFModal(true)}
                    >
                        Gestionar Categorías
                    </button>
                </div>
                <div className="form-row">
                    <label className="field">
                        <span className="label">Nombre del servicio *</span>
                        <input
                            className="input"
                            type="text"
                            value={nuevoCostoFijo.nombre}
                            onChange={(e) => setNuevoCostoFijo(n => ({ ...n, nombre: e.target.value }))}
                        />
                    </label>
                    <label className="field">
                        <span className="label">Costo</span>
                        <input
                            className="input"
                            type="number"
                            min="0"
                            step="0.01"
                            value={nuevoCostoFijo.costo}
                            onChange={(e) => setNuevoCostoFijo(n => ({ ...n, costo: parseFloat(e.target.value) || 0 }))}
                        />
                    </label>
                    <div className="field">
                        <span className="label">Categoría *</span>
                        <Select
                            styles={selectStyles}
                            options={categoriasCostoFijo.filter(c => c.activo).map(cat => ({
                                value: cat.id,
                                label: cat.nombre
                            }))}
                            value={
                                nuevoCostoFijo.categoria_id
                                    ? { value: nuevoCostoFijo.categoria_id, label: categoriasCostoFijo.find(c => c.id === parseInt(nuevoCostoFijo.categoria_id))?.nombre || '' }
                                    : null
                            }
                            onChange={(selected) => setNuevoCostoFijo(n => ({ ...n, categoria_id: selected?.value || '' }))}
                            placeholder="Seleccionar categoría..."
                            isSearchable
                            noOptionsMessage={() => 'Sin opciones'}
                        />
                    </div>
                    <button className="btn neon" type="button" onClick={agregarCostoFijo}>
                        + Agregar
                    </button>
                </div>

                <div className="divider" />

                <h3 className="h3">Costos Fijos Existentes</h3>
                <div className="admin-list">
                    {costosFijos.map(cf => (
                        <div key={cf.id} className={`admin-item ${!cf.activo ? 'inactive' : ''}`}>
                            <div className="admin-item-content">
                                <EditableInput
                                    type="text"
                                    value={cf.nombre}
                                    onSave={(val) => actualizarCostoFijo(cf.id, { nombre: val })}
                                />
                                <div className="field-inline">
                                    <span className="label">Costo:</span>
                                    <EditableInput
                                        className="input small"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={cf.costo}
                                        onSave={(val) => actualizarCostoFijo(cf.id, { costo: val })}
                                    />
                                </div>
                                <Select
                                    styles={selectStyles}
                                    options={[
                                        { value: '', label: 'Sin categoría' },
                                        ...categoriasCostoFijo.filter(c => c.activo).map(cat => ({
                                            value: cat.id,
                                            label: cat.nombre
                                        }))
                                    ]}
                                    value={
                                        cf.categoria_id
                                            ? { value: cf.categoria_id, label: categoriasCostoFijo.find(c => c.id === cf.categoria_id)?.nombre || '' }
                                            : { value: '', label: 'Sin categoría' }
                                    }
                                    onChange={(selected) => actualizarCostoFijo(cf.id, { categoria_id: selected?.value || null })}
                                    placeholder="Categoría..."
                                    isSearchable
                                    noOptionsMessage={() => 'Sin opciones'}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                            </div>
                            <div className="admin-item-actions">
                                <button
                                    className={`btn ghost small ${cf.activo ? '' : 'success'}`}
                                    onClick={() => actualizarCostoFijo(cf.id, { activo: !cf.activo })}
                                >
                                    {cf.activo ? 'Desactivar' : 'Activar'}
                                </button>
                                <button
                                    className="btn ghost small danger"
                                    onClick={() => eliminarCostoFijo(cf.id, cf.nombre)}
                                >
                                    Eliminar
                                </button>
                            </div>
                        </div>
                    ))}
                    {costosFijos.length === 0 && (
                        <p className="empty-message">No hay costos fijos creados</p>
                    )}
                </div>
            </div>
        </div>

        {/* Modal de Categorías de Costo Fijo */}
        {showCategoriasCFModal && (
            <div className="modal-overlay" onClick={() => setShowCategoriasCFModal(false)}>
                <div className="modal" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h3 className="h3">Categorías de Costo Fijo</h3>
                        <button
                            className="btn-icon"
                            onClick={() => setShowCategoriasCFModal(false)}
                        >
                            <X size={20} />
                        </button>
                    </div>
                    <div className="modal-body">
                        <div className="form-row">
                            <label className="field" style={{ flex: 1 }}>
                                <span className="label">Nueva categoría *</span>
                                <input
                                    className="input"
                                    type="text"
                                    value={nuevaCategoriaCF.nombre}
                                    onChange={(e) => setNuevaCategoriaCF(n => ({ ...n, nombre: e.target.value }))}
                                    placeholder="Nombre de la categoría"
                                />
                            </label>
                            <button
                                className="btn neon"
                                type="button"
                                onClick={agregarCategoriaCF}
                                style={{ alignSelf: 'flex-end' }}
                            >
                                + Agregar
                            </button>
                        </div>

                        <div className="divider" />

                        <div className="admin-list">
                            {categoriasCostoFijo.map(cat => (
                                <div key={cat.id} className={`admin-item ${!cat.activo ? 'inactive' : ''}`}>
                                    <div className="admin-item-content">
                                        <EditableInput
                                            type="text"
                                            value={cat.nombre}
                                            onSave={(val) => actualizarCategoriaCF(cat.id, { nombre: val })}
                                        />
                                    </div>
                                    <div className="admin-item-actions">
                                        <button
                                            className={`btn ghost small ${cat.activo ? '' : 'success'}`}
                                            onClick={() => actualizarCategoriaCF(cat.id, { activo: !cat.activo })}
                                        >
                                            {cat.activo ? 'Desactivar' : 'Activar'}
                                        </button>
                                        <button
                                            className="btn ghost small danger"
                                            onClick={() => eliminarCategoriaCF(cat.id, cat.nombre)}
                                        >
                                            Eliminar
                                        </button>
                                    </div>
                                </div>
                            ))}
                            {categoriasCostoFijo.length === 0 && (
                                <p className="empty-message">No hay categorías creadas</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        )}
    </>
    );
};

export default CostosFijosPanel;
